import React, { useEffect, useState } from 'react';
import { AppIcon } from '../../icons/appIcons.tsx';

interface Volunteer {
  key: string;
  displayName: string;
  role: string;
  familyId: string | null;
  alumniId: string | null;
  contactType: string;
  historicalAssignments: number;
}

interface DutyAssignment {
  id: string;
  dutyDate: string;
  slot: string;
  slotLabel: string;
  personKey: string | null;
  displayName: string | null;
  familyId: string | null;
  alumniId: string | null;
  contactType: string | null;
  eventId?: string | null;
  notes: string | null;
}

interface DutyDateNote {
  id?: string;
  dutyDate: string;
  eventLabel: string | null;
  additionalDuties: string | null;
}

interface DutyMonth {
  id: string;
  year: number;
  month: number;
  monthName: string;
  status: 'draft' | 'approved';
  notes: string | null;
  additionalDutiesColumnLabel: string;
  approvedAt: string | null;
  assignments: DutyAssignment[];
  dateNotes: DutyDateNote[];
}

const SLOT_ORDER = [
  'troop_mom_1',
  'troop_mom_2',
  'driving_mom',
  'troop_dad_early',
  'troop_dad_late'
];

const SLOT_HEADERS: Record<string, string> = {
  troop_mom_1: 'Troop Mom',
  troop_mom_2: 'Troop Mom',
  driving_mom: 'Driving Mom',
  troop_dad_early: 'Troop Dad - Early',
  troop_dad_late: 'Troop Dad - Late'
};

const ROLE_LABELS: Record<string, string> = {
  troop_mom: 'Troop Mom',
  troop_dad: 'Troop Dad',
  surrogate_mom: 'Surrogate Mom',
  surrogate_dad: 'Surrogate Dad'
};

export default function DutyManagement() {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [months, setMonths] = useState<DutyMonth[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<DutyMonth | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const now = new Date();
  const [genYear, setGenYear] = useState(now.getFullYear());
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}`);
    }
    return data;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [volData, monthData] = await Promise.all([
        authenticatedFetch('/api/duties/volunteers'),
        authenticatedFetch('/api/duties/months')
      ]);
      setVolunteers(volData.data || []);
      setMonths(monthData.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load duty data');
    } finally {
      setLoading(false);
    }
  };

  const loadMonth = async (id: string) => {
    const data = await authenticatedFetch(`/api/duties/months/${id}`);
    setSelectedMonth(data.data);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleGenerate = async () => {
    if (!window.confirm(`Generate a fair draft duty list for ${genMonth}/${genYear}?`)) return;
    try {
      setWorking(true);
      setMessage('');
      setError('');
      const data = await authenticatedFetch('/api/duties/months/generate', {
        method: 'POST',
        body: JSON.stringify({ year: genYear, month: genMonth })
      });
      setMessage(data.message);
      await loadData();
      await loadMonth(data.data.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  const monthHasCalendarEvents = (month: DutyMonth | null) =>
    Boolean(month?.assignments?.some(a => a.eventId));

  const handleReopen = async () => {
    if (!selectedMonth) return;
    if (!window.confirm(
      'Edit this approved duty list? Calendar events for this month will be hidden until you re-approve.'
    )) return;
    try {
      setWorking(true);
      setError('');
      const data = await authenticatedFetch(`/api/duties/months/${selectedMonth.id}/reopen`, {
        method: 'POST'
      });
      setMessage(data.message);
      await loadData();
      await loadMonth(selectedMonth.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedMonth) return;
    const isUpdate = monthHasCalendarEvents(selectedMonth);
    if (!window.confirm(
      isUpdate
        ? 'Re-approve this duty list? Calendar events will be updated with your changes.'
        : 'Approve this duty list? This will publish calendar events for each Saturday.'
    )) return;
    try {
      setWorking(true);
      setError('');
      const data = await authenticatedFetch(`/api/duties/months/${selectedMonth.id}/approve`, {
        method: 'POST'
      });
      setMessage(data.message);
      await loadData();
      await loadMonth(selectedMonth.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  const handleExport = async () => {
    if (!selectedMonth) return;
    const token = localStorage.getItem('authToken');
    const response = await fetch(`/api/duties/months/${selectedMonth.id}/export`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      setError('Export failed');
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GGAA_Duty_List_${selectedMonth.year}_${String(selectedMonth.month).padStart(2, '0')}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    if (!selectedMonth || selectedMonth.status !== 'draft') return;
    if (!window.confirm('Delete this draft duty month?')) return;
    try {
      setWorking(true);
      await authenticatedFetch(`/api/duties/months/${selectedMonth.id}`, { method: 'DELETE' });
      setSelectedMonth(null);
      setMessage('Draft deleted');
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  };

  const getDateNote = (date: string): DutyDateNote => {
    const existing = selectedMonth?.dateNotes?.find(n => n.dutyDate === date);
    return existing || { dutyDate: date, eventLabel: null, additionalDuties: null };
  };

  const handleColumnLabelBlur = async () => {
    if (!selectedMonth) return;
    try {
      await authenticatedFetch(`/api/duties/months/${selectedMonth.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          additionalDutiesColumnLabel: selectedMonth.additionalDutiesColumnLabel
        })
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateLocalDateNote = (
    dutyDate: string,
    field: 'eventLabel' | 'additionalDuties',
    value: string
  ) => {
    if (!selectedMonth) return;

    const updatedNotes = [...(selectedMonth.dateNotes || [])];
    const idx = updatedNotes.findIndex(n => n.dutyDate === dutyDate);
    const note = idx >= 0
      ? { ...updatedNotes[idx], [field]: value || null }
      : { dutyDate, eventLabel: null, additionalDuties: null, [field]: value || null };

    if (idx >= 0) {
      updatedNotes[idx] = note;
    } else {
      updatedNotes.push(note);
    }

    setSelectedMonth({ ...selectedMonth, dateNotes: updatedNotes });
  };

  const saveDateNote = async (
    dutyDate: string,
    overrides?: Partial<Pick<DutyDateNote, 'eventLabel' | 'additionalDuties'>>
  ) => {
    if (!selectedMonth) return;
    const note = { ...getDateNote(dutyDate), ...overrides };
    try {
      await authenticatedFetch(`/api/duties/months/${selectedMonth.id}/date-notes`, {
        method: 'PUT',
        body: JSON.stringify({
          dutyDate,
          eventLabel: note.eventLabel,
          additionalDuties: note.additionalDuties
        })
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAssignmentChange = async (assignment: DutyAssignment, volunteerKey: string) => {
    if (!selectedMonth || selectedMonth.status === 'approved') return;
    const volunteer = volunteers.find(v => v.key === volunteerKey);
    try {
      await authenticatedFetch(`/api/duties/assignments/${assignment.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          personKey: volunteerKey || null,
          displayName: volunteer ? volunteer.displayName : null,
          familyId: volunteer ? volunteer.familyId : null,
          alumniId: volunteer ? volunteer.alumniId : null,
          contactType: volunteer ? volunteer.contactType : null
        })
      });
      await loadMonth(selectedMonth.id);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const volunteersForSlot = (slot: string) => {
    if (slot.includes('mom')) {
      return volunteers.filter(v => v.role === 'troop_mom' || v.role === 'surrogate_mom');
    }
    return volunteers.filter(v => v.role === 'troop_dad' || v.role === 'surrogate_dad');
  };

  const groupedByDate = () => {
    if (!selectedMonth) return [];
    const map: Record<string, DutyAssignment[]> = {};
    for (const a of selectedMonth.assignments) {
      if (!map[a.dutyDate]) map[a.dutyDate] = [];
      map[a.dutyDate].push(a);
    }
    return Object.keys(map).sort().map(date => ({
      date,
      slots: SLOT_ORDER.map(slot => map[date].find(a => a.slot === slot)).filter(Boolean) as DutyAssignment[]
    }));
  };

  const troopMomCount = volunteers.filter(v => v.role === 'troop_mom').length;
  const troopDadCount = volunteers.filter(v => v.role === 'troop_dad').length;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-6 lg:py-10">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <AppIcon name="duties" className="w-7 h-7 text-ggaa-green shrink-0" />
          Duty List Management
        </h2>
        <p className="text-gray-600 mt-1">
          Tag troop parents on family and associated member profiles, then auto-generate fair Saturday duty schedules.
        </p>
      </div>

      {message && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">{message}</div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow border p-4">
          <h3 className="font-semibold text-gray-900">Troop Moms</h3>
          <p className="text-3xl font-bold text-ggaa-green mt-1">{troopMomCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow border p-4">
          <h3 className="font-semibold text-gray-900">Troop Dads</h3>
          <p className="text-3xl font-bold text-blue-600 mt-1">{troopDadCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow border p-4">
          <h3 className="font-semibold text-gray-900">Surrogates</h3>
          <p className="text-3xl font-bold text-purple-600 mt-1">
            {volunteers.filter(v => v.role.startsWith('surrogate')).length}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border mb-6 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Monthly Duty List</h3>
        <p className="text-sm text-gray-600 mb-4">
          Assign duty roles under <strong>Manage Families</strong> (primary/secondary contact) and{' '}
          <strong>Associated Members</strong> (surrogate roles). Generation spreads duties evenly using approved history.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <input
              type="number"
              value={genYear}
              onChange={e => setGenYear(parseInt(e.target.value, 10))}
              className="border rounded-lg px-3 py-2 w-28"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={genMonth}
              onChange={e => setGenMonth(parseInt(e.target.value, 10))}
              className="border rounded-lg px-3 py-2"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={working}
            className="px-5 py-2 bg-ggaa-green text-white rounded-lg font-medium hover:bg-ggaa-dark disabled:opacity-50"
          >
            {working ? 'Working…' : 'Auto-Generate Draft'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-1 bg-white rounded-xl shadow border p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Duty Months</h3>
          {months.length === 0 ? (
            <p className="text-sm text-gray-500">No duty lists yet.</p>
          ) : (
            <ul className="space-y-2">
              {months.map(m => (
                <li key={m.id}>
                  <button
                    onClick={() => loadMonth(m.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      selectedMonth?.id === m.id
                        ? 'border-ggaa-green bg-green-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">{m.monthName} {m.year}</div>
                    <div className="text-xs text-gray-500 capitalize">{m.status}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="xl:col-span-3">
          {selectedMonth ? (
            <div className="bg-white rounded-xl shadow border p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedMonth.monthName} {selectedMonth.year}
                  </h3>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                    selectedMonth.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedMonth.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleExport}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Export Excel
                  </button>
                  {selectedMonth.status === 'approved' && (
                    <button
                      onClick={handleReopen}
                      disabled={working}
                      className="px-4 py-2 border border-ggaa-green text-ggaa-green rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50"
                    >
                      Edit Duty List
                    </button>
                  )}
                  {selectedMonth.status === 'draft' && (
                    <>
                      <button
                        onClick={handleApprove}
                        disabled={working}
                        className="px-4 py-2 bg-ggaa-green text-white rounded-lg text-sm font-medium hover:bg-ggaa-dark disabled:opacity-50"
                      >
                        {monthHasCalendarEvents(selectedMonth)
                          ? 'Re-approve & Update Calendar'
                          : 'Approve & Add to Calendar'}
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={working}
                        className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
                      >
                        Delete Draft
                      </button>
                    </>
                  )}
                </div>
              </div>

              {selectedMonth.status === 'draft' && monthHasCalendarEvents(selectedMonth) && (
                <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This month is in draft mode. Save your changes, then re-approve to update the calendar.
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional duties column header (e.g. &quot;RENN FEST ASSIGNMENT&quot;)
                </label>
                <input
                  type="text"
                  value={selectedMonth.additionalDutiesColumnLabel || 'Additional Duties'}
                  onChange={e => setSelectedMonth({
                    ...selectedMonth,
                    additionalDutiesColumnLabel: e.target.value
                  })}
                  onBlur={handleColumnLabelBlur}
                  disabled={selectedMonth.status === 'approved'}
                  className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                  placeholder="Additional Duties"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-3 py-2 text-left">Date</th>
                      <th className="border px-3 py-2 text-left">Event</th>
                      {SLOT_ORDER.map(slot => (
                        <th key={slot} className="border px-3 py-2 text-left">{SLOT_HEADERS[slot]}</th>
                      ))}
                      <th className="border px-3 py-2 text-left min-w-[180px]">
                        {selectedMonth.additionalDutiesColumnLabel || 'Additional Duties'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedByDate().map(row => {
                      const dateNote = getDateNote(row.date);
                      return (
                        <tr key={row.date}>
                          <td className="border px-3 py-2 font-medium whitespace-nowrap">
                            {new Date(row.date + 'T12:00:00').toLocaleDateString()}
                          </td>
                          <td className="border px-2 py-1">
                            {selectedMonth.status === 'draft' ? (
                              <input
                                type="text"
                                value={dateNote.eventLabel || ''}
                                onChange={e => updateLocalDateNote(row.date, 'eventLabel', e.target.value)}
                                onBlur={e => saveDateNote(row.date, { eventLabel: e.target.value || null })}
                                placeholder="e.g. Horse Show"
                                className="w-full border rounded px-2 py-1 text-sm"
                              />
                            ) : (
                              <span className="text-sm text-gray-700">{dateNote.eventLabel || '—'}</span>
                            )}
                          </td>
                          {SLOT_ORDER.map(slot => {
                            const assignment = row.slots.find(s => s.slot === slot);
                            if (!assignment) return <td key={slot} className="border px-3 py-2" />;
                            return (
                              <td key={slot} className="border px-2 py-1">
                                {selectedMonth.status === 'draft' ? (
                                  <select
                                    value={assignment.personKey || ''}
                                    onChange={e => handleAssignmentChange(assignment, e.target.value)}
                                    className="w-full border rounded px-2 py-1 text-sm"
                                  >
                                    <option value="">— Unassigned —</option>
                                    {volunteersForSlot(slot).map(v => (
                                      <option key={v.key} value={v.key}>
                                        {v.displayName} ({ROLE_LABELS[v.role]}, {v.historicalAssignments} past)
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span>{assignment.displayName || '—'}</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="border px-2 py-1">
                            {selectedMonth.status === 'draft' ? (
                              <textarea
                                value={dateNote.additionalDuties || ''}
                                onChange={e => updateLocalDateNote(row.date, 'additionalDuties', e.target.value)}
                                onBlur={e => saveDateNote(row.date, { additionalDuties: e.target.value || null })}
                                placeholder="e.g. Sprung, Valleroy, Bayer"
                                rows={2}
                                className="w-full border rounded px-2 py-1 text-sm min-w-[160px]"
                              />
                            ) : (
                              <span className="text-sm text-gray-700 whitespace-pre-wrap">
                                {dateNote.additionalDuties || '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow border p-8 text-center text-gray-500">
              Select a duty month or generate a new draft to get started.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <strong>How it works:</strong> Tag contacts as Troop Mom/Dad on active ranch families (Manage Families).
        Surrogate Mom/Dad can be set on families or associated members. Each Saturday gets 2 troop moms, 1 driving mom,
        and 2 troop dads (early/late). Approve to publish ranch duty events on the calendar.
      </div>
    </div>
  );
}
